import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VISITOR_KEY = 'zr_visitor_id';
const LANDING_KEY = 'zr_landing_url';

const MARKETPLACE_KEYS = [
  'myntra', 'nykaa', 'tira', 'ajio', 'firstcry', 'bigbasket',
  'noon', 'namshi', 'ounass', 'blinkit', 'zepto', 'instamart', 'amazon', 'flipkart',
];

const getOrCreateVisitorId = () => {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = 'v_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return 'v_anon_' + Math.random().toString(36).slice(2, 10);
  }
};

const detectPageType = (pathname) => {
  if (pathname === '/' || pathname === '/home') return 'home';
  if (pathname.startsWith('/blog')) return 'blog';
  if (pathname.includes('-vs-')) return 'comparison';
  // marketplace page detection — sprint 4 pages are *-seller-* / *-brand-* / *-account-* slugs
  if (
    pathname.includes('-seller-') ||
    pathname.includes('-brand-') ||
    pathname.includes('-account-') ||
    pathname.startsWith('/marketplaces')
  ) return 'marketplace';
  return 'other';
};

const detectMarketplaceKey = (pathname) => {
  const lower = pathname.toLowerCase();
  for (const k of MARKETPLACE_KEYS) {
    if (lower.includes('/' + k) || lower.includes('-' + k) || lower.startsWith('/' + k + '-')) {
      return k;
    }
  }
  return null;
};

const getUtm = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
    };
  } catch {
    return { utm_source: null, utm_medium: null, utm_campaign: null };
  }
};

/**
 * Mount once inside <BrowserRouter>. Fires a POST /api/attribution/track for every
 * pathname change so we can rebuild the visitor → marketplace → lead → revenue funnel
 * in the admin Revenue Attribution dashboard.
 */
export const useAttributionTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (!BACKEND_URL) return;
    if (typeof window === 'undefined') return;
    // Skip admin paths — we don't want to attribute admin traffic to public funnel
    if (location.pathname.startsWith('/admin')) return;

    const visitor_id = getOrCreateVisitorId();
    let is_landing = false;
    try {
      if (!localStorage.getItem(LANDING_KEY)) {
        localStorage.setItem(LANDING_KEY, location.pathname);
        is_landing = true;
      }
    } catch { /* localStorage blocked */ }

    const payload = {
      visitor_id,
      path: location.pathname,
      page_type: detectPageType(location.pathname),
      marketplace_key: detectMarketplaceKey(location.pathname),
      referrer: typeof document !== 'undefined' ? document.referrer || null : null,
      is_landing,
      ...getUtm(),
    };

    try {
      // fire-and-forget; keepalive lets it survive navigations
      fetch(`${BACKEND_URL}/api/attribution/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* ignore */ });
    } catch { /* ignore */ }
  }, [location.pathname]);
};

export const getVisitorId = () => {
  try { return localStorage.getItem(VISITOR_KEY); } catch { return null; }
};

/** Call from any lead-submission flow to associate visitor → lead. */
export const linkLeadToVisitor = async (lead_id, lead_type = 'qualified') => {
  if (!BACKEND_URL || !lead_id) return;
  const visitor_id = getVisitorId();
  if (!visitor_id) return;
  try {
    await fetch(`${BACKEND_URL}/api/attribution/link-lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitor_id, lead_id, lead_type }),
      keepalive: true,
    });
  } catch { /* ignore */ }
};
